<template>
  <div class="industry-setup">
    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span>🎯 行业采集配置</span>
          <span class="sub">选择行业 → 自动加载对应采集/打标关键词，可自定义补充</span>
        </div>
      </template>

      <!-- 账号选择 -->
      <div class="row">
        <span class="label">选择账号：</span>
        <el-select v-model="selectedAccount" placeholder="选择要配置的账号" style="width: 320px" @change="onAccountChange">
          <el-option v-for="acc in accounts" :key="acc.id" :label="`${acc.nickname}（${acc.username}）`" :value="acc.id" />
        </el-select>
        <el-tag v-if="currentIndustry" type="success" size="large" style="margin-left: 12px">
          当前行业：{{ currentIndustry }}
        </el-tag>
      </div>

      <el-divider />

      <!-- 行业卡片 -->
      <div class="row"><span class="label">选择行业：</span></div>

      <!-- 分组筛选 + 关键词搜索（v2 67 类目，必须能分组） -->
      <div class="industry-toolbar">
        <div class="group-chips">
          <div class="chip" :class="{ active: activeGroup === 'all' }" @click="activeGroup = 'all'">
            全部 <span class="chip-n">{{ allCount }}</span>
          </div>
          <div
            v-for="g in groups"
            :key="g.id"
            class="chip"
            :class="{ active: activeGroup === g.id, b2b: g.b2b }"
            @click="activeGroup = g.id"
          >
            {{ g.name }} <span class="chip-n">{{ g.industries.length }}</span>
            <el-tag v-if="g.b2b" size="small" type="warning" class="chip-badge">B2B</el-tag>
            <el-tag v-else size="small" type="success" class="chip-badge">B2C</el-tag>
          </div>
        </div>
        <el-input
          v-model="searchKeyword"
          placeholder="搜索行业名 / 二级类目，如：UV打印、护肤、模具"
          clearable
          size="small"
          class="industry-search"
        >
          <template #prefix><span>🔍</span></template>
        </el-input>
      </div>

      <div class="industry-grid">
        <div
          v-for="name in filteredNames"
          :key="name"
          class="industry-card"
          :class="{ active: selectedIndustry === name }"
          @click="selectIndustry(name)"
        >
          <div class="ic-top">
            <div class="ic-name">{{ name }}</div>
            <el-tag v-if="entryOf(name)?.b2b" size="small" type="warning" class="ic-badge">B2B</el-tag>
            <el-tag v-else-if="entryOf(name)" size="small" type="success" class="ic-badge">B2C</el-tag>
          </div>
          <div v-if="entryOf(name)?.group_name" class="ic-group">{{ entryOf(name).group_name }}</div>
          <div v-if="entryOf(name)?.sub?.length" class="ic-sub">
            <span v-for="s in entryOf(name).sub.slice(0, 4)" :key="s" class="ic-sub-item">{{ s }}</span>
            <span v-if="entryOf(name).sub.length > 4" class="ic-sub-more">+{{ entryOf(name).sub.length - 4 }}</span>
          </div>
          <div class="ic-meta">
            <el-tag size="small" type="primary">{{ (industries[name]?.room || []).length }} 搜索词</el-tag>
            <el-tag size="small" type="danger">{{ (industries[name]?.intent_A || []).length }} A级</el-tag>
            <el-tag size="small" type="warning">{{ (industries[name]?.intent_B || []).length }} B级</el-tag>
          </div>
        </div>
        <div v-if="!filteredNames.length" class="empty-hint">没有匹配的行业</div>
      </div>

      <div v-if="filteredNames.length" class="taxo-foot">
        共 {{ filteredNames.length }} 个行业 ｜ B2B {{ filteredB2B }} / B2C {{ filteredB2C }}
        ｜ 当前显示 <b>{{ activeGroup === 'all' ? '全部分组' : groupName(activeGroup) }}</b>
      </div>

      <div class="row" style="margin-top: 16px">
        <el-button type="primary" size="large" :loading="saving" @click="saveIndustry">
          保存行业配置
        </el-button>
        <el-button v-if="currentIndustry" size="large" @click="loadCustom">刷新自定义词</el-button>
      </div>
    </el-card>

    <!-- 词库详情 -->
    <el-card v-if="selectedWords" shadow="never" style="margin-top: 16px">
      <template #header>
        <span>📚 {{ selectedIndustry }} 词库详情</span>
      </template>

      <!-- 二级类目（v2：细分方向，用于定位账号内容与选品） -->
      <div v-if="currentEntry && currentEntry.sub && currentEntry.sub.length" class="word-section">
        <div class="ws-title">🧭 高频二级类目（细分方向）</div>
        <div class="ws-hint">{{ currentEntry.intent_model_hint || '可作为账号内容定位与直播间选题方向' }}</div>
        <el-tag
          v-for="s in currentEntry.sub"
          :key="s"
          size="small"
          class="word-tag"
          type="info"
          effect="plain"
        >{{ s }}</el-tag>
      </div>

      <div class="word-section">
        <div class="ws-title">🔍 直播间/账号搜索词（room）</div>
        <el-tag v-for="w in selectedWords.room" :key="w" size="small" class="word-tag" type="primary">{{ w }}</el-tag>
      </div>

      <div class="word-section">
        <div class="ws-title">🎯 A级意向词（命中=5分强意向，优先跟进）</div>
        <el-tag v-for="w in selectedWords.intent_A" :key="w" size="small" class="word-tag" type="danger">{{ w }}</el-tag>
      </div>

      <div class="word-section">
        <div class="ws-title">👀 B级普通词（命中=0.6分，养熟）</div>
        <el-tag v-for="w in selectedWords.intent_B" :key="w" size="small" class="word-tag" type="warning">{{ w }}</el-tag>
      </div>

      <!-- 自定义词 -->
      <el-divider />
      <div class="word-section">
        <div class="ws-title">✏️ 自定义补充词（仅当前账号生效）</div>
        <div class="custom-add">
          <el-input v-model="newWord" placeholder="输入补充词，如：设备能租吗" style="width: 280px" clearable @keyup.enter="addCustomWord" />
          <el-select v-model="newWordLevel" style="width: 120px; margin: 0 8px">
            <el-option label="A级(强意向)" value="A" />
            <el-option label="B级(普通)" value="B" />
          </el-select>
          <el-button type="success" @click="addCustomWord">添加</el-button>
        </div>
        <div v-if="customWords.length" style="margin-top: 10px">
          <el-tag
            v-for="(w, i) in customWords"
            :key="w.word"
            size="small"
            closable
            class="word-tag"
            :type="w.level === 'A' ? 'danger' : 'warning'"
            @close="removeCustomWord(i, w.word)"
          >
            {{ w.word }}（{{ w.level }}级）
          </el-tag>
        </div>
        <div v-else style="margin-top: 8px; color: #999">暂无自定义词，添加后打标立即生效</div>
      </div>
    </el-card>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'IndustrySetup',
  data() {
    return {
      accounts: [],
      selectedAccount: null,
      industries: {},
      taxonomy: null,          // v2 分类元数据：groups / entries / alias
      groups: [],              // [{id,name,desc,b2b,industries[]}]
      activeGroup: 'all',      // 分组筛选
      searchKeyword: '',       // 行业名/二级类目搜索
      selectedIndustry: '',
      currentIndustry: '',
      saving: false,
      customWords: [],
      newWord: '',
      newWordLevel: 'A',
    }
  },
  computed: {
    selectedWords() {
      return (this.industries || {})[this.selectedIndustry] || null
    },
    entries() {
      return (this.taxonomy && this.taxonomy.entries) || {}
    },
    allCount() {
      return Object.keys(this.industries || {}).length
    },
    currentEntry() {
      const e = this.entries[this.selectedIndustry]
      if (!e) return null
      const g = (this.groups || []).find(x => x.id === e.group)
      return { ...e, intent_model_hint: g ? g.intent_model : '' }
    },
    filteredNames() {
      const names = Object.keys(this.industries || {})
      const kw = (this.searchKeyword || '').trim().toLowerCase()
      return names.filter(n => {
        if (this.activeGroup !== 'all' && this.entryOf(n) && this.entryOf(n).group !== this.activeGroup) {
          return false
        }
        if (!kw) return true
        const subs = (this.entryOf(n) && this.entryOf(n).sub) || []
        return n.toLowerCase().includes(kw) || subs.some(s => s.toLowerCase().includes(kw))
      })
    },
    filteredB2B() {
      return this.filteredNames.filter(n => this.entryOf(n) && this.entryOf(n).b2b).length
    },
    filteredB2C() {
      return this.filteredNames.filter(n => this.entryOf(n) && !this.entryOf(n).b2b).length
    },
  },
  mounted() {
    this.loadAccounts()
    this.loadIndustries()
    this.loadTaxonomy()
  },
  methods: {
    entryOf(name) {
      return this.entries[name] || null
    },
    groupName(gid) {
      const g = (this.groups || []).find(x => x.id === gid)
      return g ? g.name : gid
    },
    async loadTaxonomy() {
      try {
        const r = await axios.get('/api/v1/industries/taxonomy')
        this.taxonomy = r.data
        this.groups = r.data.groups || []
      } catch (e) {
        console.error('taxonomy 加载失败（页面仍可降级使用）:', e)
      }
    },
    async loadAccounts() {
      try {
        const r = await axios.get('/api/v1/accounts/')
        this.accounts = Array.isArray(r.data) ? r.data : (r.data?.accounts || [])
        if (this.accounts.length) {
          this.selectedAccount = this.accounts[0].id
          this.onAccountChange()
        }
      } catch (e) { console.error(e) }
    },
    async loadIndustries() {
      try {
        // 注意：接口路径必须带尾斜杠（router 定义为 /api/v1/industries/）
        const r = await axios.get('/api/v1/industries/')
        // v2 词库里有 _ 前缀元数据键（_说明/_别名/_自采行业），后端重启后会自动过滤，
        // 前端这里也兜一层，避免过渡期显示成假行业卡片
        const raw = (r.data && r.data.industries) || r.data || {}
        this.industries = Object.fromEntries(
          Object.entries(raw).filter(([k]) => !String(k).startsWith('_'))
        )
      } catch (e) { console.error(e) }
    },
    async onAccountChange() {
      if (!this.selectedAccount) return
      try {
        const r = await axios.get(`/api/v1/industries/accounts/${this.selectedAccount}/industry`)
        this.currentIndustry = r.data.industry
        this.selectedIndustry = r.data.industry || ''
        this.loadCustom()
      } catch (e) { console.error(e) }
    },
    selectIndustry(name) {
      this.selectedIndustry = name
    },
    async saveIndustry() {
      if (!this.selectedAccount || !this.selectedIndustry) {
        this.$message.warning('请先选择账号和行业')
        return
      }
      this.saving = true
      try {
        await axios.put(`/api/v1/industries/accounts/${this.selectedAccount}/industry`, {
          industry: this.selectedIndustry
        })
        this.currentIndustry = this.selectedIndustry
        this.$message.success(`已保存：${this.selectedIndustry}`)
        this.loadCustom()
      } catch (e) {
        this.$message.error('保存失败：' + (e.response?.data?.detail || e.message))
      } finally {
        this.saving = false
      }
    },
    async loadCustom() {
      if (!this.selectedAccount) return
      try {
        const r = await axios.get(`/api/v1/industries/custom/list?account_id=${this.selectedAccount}`)
        this.customWords = r.data.words
      } catch (e) { console.error(e) }
    },
    async addCustomWord() {
      const word = this.newWord.trim()
      if (!word) { this.$message.warning('请输入补充词'); return }
      try {
        await axios.post('/api/v1/industries/custom', {
          account_id: this.selectedAccount,
          industry: this.currentIndustry,
          word,
          level: this.newWordLevel
        })
        this.$message.success(`已添加：${word}`)
        this.newWord = ''
        this.loadCustom()
      } catch (e) {
        this.$message.error(e.response?.data?.detail || '添加失败')
      }
    },
    async removeCustomWord(index, word) {
      try {
        await axios.delete(`/api/v1/industries/custom/${this.customWords[index].id}`)
        this.$message.success(`已删除：${word}`)
        this.loadCustom()
      } catch (e) {
        this.$message.error('删除失败')
      }
    }
  }
}
</script>

<style scoped>
.card-header { display: flex; align-items: baseline; gap: 12px; }
.card-header .sub { font-size: 13px; color: #888; }
.row { display: flex; align-items: center; margin-bottom: 8px; }
.label { font-weight: 600; margin-right: 8px; }

/* 分组筛选 + 搜索 */
.industry-toolbar {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  margin: 8px 0 12px; padding: 10px 12px;
  background: #0e1526; border: 1px solid rgba(148,163,184,.15); border-radius: 8px;
}
.group-chips { display: flex; gap: 8px; flex-wrap: wrap; flex: 1; }
.chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 16px; font-size: 13px; cursor: pointer;
  border: 1px solid rgba(148,163,184,.3); background: #16203a; color: #cbd5e1;
  transition: all .15s; user-select: none;
}
.chip:hover { border-color: #2563EB; color: #fff; }
.chip.active { border-color: #2563EB; background: #1d3b7a; color: #fff; box-shadow: 0 2px 8px rgba(37,99,235,.25); }
.chip.b2b.active { border-color: #f59e0b; background: #4a3311; box-shadow: 0 2px 8px rgba(245,158,11,.22); }
.chip-n {
  font-size: 11px; padding: 0 6px; border-radius: 8px;
  background: rgba(148,163,184,.25); color: #94a3b8;
}
.chip.active .chip-n { background: rgba(255,255,255,.22); color: #fff; }
.chip-badge { margin-left: 2px; padding: 0 4px; height: 18px; line-height: 18px; transform: scale(.85); }
.industry-search { width: 300px; flex-shrink: 0; }

.industry-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 8px; }
.industry-card {
  border: 1px solid rgba(148,163,184,.2); border-radius: 8px; padding: 12px 12px;
  cursor: pointer; transition: all 0.2s; background: #0e1526;
}
.industry-card:hover { border-color: #2563EB; box-shadow: 0 2px 8px rgba(24,144,255,0.15); }
.industry-card.active { border-color: #2563EB; background: #16203a; box-shadow: 0 2px 8px rgba(24,144,255,0.25); }
.ic-top { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
.ic-name { font-size: 15px; font-weight: 600; flex: 1; }
.ic-badge { transform: scale(.85); transform-origin: right center; }
.ic-group { font-size: 11.5px; color: #64748b; margin-bottom: 6px; }
.ic-sub { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 8px; }
.ic-sub-item {
  font-size: 11px; padding: 1px 6px; border-radius: 4px;
  background: rgba(148,163,184,.14); color: #94a3b8;
}
.ic-sub-more { font-size: 11px; color: #475569; padding: 1px 4px; }
.ic-meta { display: flex; gap: 6px; flex-wrap: wrap; }
.empty-hint { grid-column: 1 / -1; text-align: center; color: #64748b; padding: 28px 0; font-size: 14px; }
.taxo-foot { margin-top: 12px; font-size: 12.5px; color: #94a3b8; }

/* 二级类目 + 词库 */
.ws-hint { font-size: 12px; color: #94a3b8; margin-bottom: 8px; }
.word-section { margin-bottom: 16px; }
.ws-title { font-weight: 600; margin-bottom: 8px; color: #e2e8f0; }
.word-tag { margin: 0 6px 6px 0; }
.custom-add { display: flex; align-items: center; }

@media (max-width: 1100px) { .industry-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 768px) {
  .industry-grid { grid-template-columns: repeat(2, 1fr); }
  .industry-search { width: 100%; }
  .industry-toolbar { flex-direction: column; align-items: stretch; }
}
</style>
